import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { BarChart3, Check, Clock, EyeOff, Loader2, Lock } from "@fg/icons";
import { ConnectApiError, connectPostsApi } from "@/api/connectApi";
import { usePoll } from "@/api/hooks";
import { useAccessToken } from "@/auth/useAccessToken";
import { cn } from "@/lib/utils";

function closesLabel(iso: string): string {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleString(undefined, { weekday: "short", day: "numeric", month: "short", hour: "numeric", minute: "2-digit" });
}

/**
 * A poll posted in a conversation: live results as bars, your choice ticked,
 * click to vote (click your choice again to withdraw; multiple-choice polls
 * toggle each option). The creator can close it. Anonymous polls show counts
 * only. Results refresh on the hub's poll.updated event (and a slow poll).
 */
export function PollCard({ pollId }: { pollId: string }) {
    const token = useAccessToken();
    const qc = useQueryClient();
    const query = usePoll(pollId);
    const [confirmClose, setConfirmClose] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const refresh = () => qc.invalidateQueries({ queryKey: ["poll", pollId] });
    const failure = (e: unknown, fallback: string) =>
        setError(e instanceof Error && e.message && !e.message.startsWith("Connect BFF") ? e.message : fallback);

    const vote = useMutation({
        mutationFn: (optionIds: string[]) => connectPostsApi.votePoll(pollId, optionIds, token),
        onMutate: () => setError(null),
        onSuccess: () => void refresh(),
        onError: (e) => {
            failure(e, "Couldn't record your vote. Try again.");
            void refresh();
        }
    });
    const close = useMutation({
        mutationFn: () => connectPostsApi.closePoll(pollId, token),
        onMutate: () => setError(null),
        onSuccess: () => {
            setConfirmClose(false);
            void refresh();
        },
        onError: (e) => failure(e, "Couldn't close the poll. Try again.")
    });

    if (query.isPending) {
        return (
            <div className="my-1.5 flex w-full max-w-md items-center gap-2 rounded-lg border bg-card p-3 text-xs text-muted-foreground">
                <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> Loading poll…
            </div>
        );
    }
    if (query.isError || !query.data) {
        const status = query.error instanceof ConnectApiError ? query.error.status : 0;
        return (
            <div role="note" className="my-1.5 flex w-full max-w-md items-center gap-2 rounded-lg border border-dashed bg-card p-3 text-xs text-muted-foreground">
                <BarChart3 className="h-4 w-4" aria-hidden />
                {status === 404 ? "This poll isn't available to you." : "Couldn't load this poll."}
            </div>
        );
    }

    const poll = query.data;
    const mine = new Set(poll.myVotes);
    const denominator = Math.max(1, poll.totalVoters);
    const leading = Math.max(0, ...poll.options.map((o) => o.votes));

    const choose = (optionId: string) => {
        if (poll.isClosed || vote.isPending) return;
        const next = poll.allowMultiple
            ? (mine.has(optionId) ? poll.myVotes.filter((v) => v !== optionId) : [...poll.myVotes, optionId])
            : (mine.has(optionId) ? [] : [optionId]);
        vote.mutate(next);
    };

    return (
        <section
            aria-label={`Poll: ${poll.question}`}
            className="my-1.5 w-full max-w-md rounded-lg border bg-card p-3 text-card-foreground shadow-sm"
        >
            <header className="mb-2">
                <div className="flex flex-wrap items-center gap-1.5 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                    <BarChart3 className="h-3.5 w-3.5 text-primary" aria-hidden />
                    Poll
                    {poll.allowMultiple ? <span className="rounded-full bg-muted px-1.5 py-0.5 normal-case tracking-normal">Multiple choice</span> : null}
                    {poll.isAnonymous ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-muted px-1.5 py-0.5 normal-case tracking-normal">
                            <EyeOff className="h-3 w-3" aria-hidden /> Anonymous
                        </span>
                    ) : null}
                    {poll.isClosed ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-muted px-1.5 py-0.5 normal-case tracking-normal">
                            <Lock className="h-3 w-3" aria-hidden /> Closed
                        </span>
                    ) : poll.closesAt ? (
                        <span className="inline-flex items-center gap-1 normal-case tracking-normal">
                            <Clock className="h-3 w-3" aria-hidden /> Closes {closesLabel(poll.closesAt)}
                        </span>
                    ) : null}
                </div>
                <h3 className="mt-1 break-words text-sm font-semibold">{poll.question}</h3>
            </header>

            <ul className="space-y-1.5" role="list">
                {poll.options.map((o) => {
                    const pct = Math.round((o.votes / denominator) * 100);
                    const chosen = mine.has(o.id);
                    const names = (o.voters ?? []).map((v) => v.name || "Someone");
                    const label = `${o.label}: ${o.votes} ${o.votes === 1 ? "vote" : "votes"}, ${pct}%${chosen ? ", your choice" : ""}`;
                    return (
                        <li key={o.id}>
                            <button
                                type="button"
                                onClick={() => choose(o.id)}
                                disabled={poll.isClosed || vote.isPending}
                                aria-pressed={chosen}
                                aria-label={label}
                                title={!poll.isAnonymous && names.length > 0 ? names.slice(0, 15).join(", ") + (names.length > 15 ? "…" : "") : undefined}
                                className={cn(
                                    "relative block w-full overflow-hidden rounded-md border px-2.5 py-2 text-left text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
                                    chosen ? "border-primary" : "border-input hover:border-primary/50",
                                    poll.isClosed ? "cursor-default" : "hover:bg-accent/40",
                                    "disabled:opacity-100"
                                )}
                            >
                                <span
                                    aria-hidden
                                    className={cn(
                                        "absolute inset-y-0 left-0 transition-[width] duration-300",
                                        chosen ? "bg-primary/20 dark:bg-primary/30" : "bg-muted",
                                        poll.isClosed && o.votes === leading && leading > 0 && "bg-emerald-500/20 dark:bg-emerald-500/25"
                                    )}
                                    style={{ width: `${pct}%` }}
                                />
                                <span className="relative flex items-center gap-2">
                                    <span
                                        aria-hidden
                                        className={cn(
                                            "flex h-4 w-4 shrink-0 items-center justify-center border",
                                            poll.allowMultiple ? "rounded-sm" : "rounded-full",
                                            chosen ? "border-primary bg-primary text-primary-foreground" : "border-muted-foreground/50 bg-background"
                                        )}
                                    >
                                        {chosen ? <Check className="h-3 w-3" /> : null}
                                    </span>
                                    <span className="min-w-0 flex-1 break-words font-medium">{o.label}</span>
                                    <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                                        {o.votes} · {pct}%
                                    </span>
                                </span>
                            </button>
                        </li>
                    );
                })}
            </ul>

            <footer className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-muted-foreground">
                <span>
                    {poll.totalVoters} {poll.totalVoters === 1 ? "person" : "people"} voted
                    {poll.createdByName ? ` · by ${poll.createdByName}` : ""}
                </span>
                {vote.isPending ? <Loader2 className="h-3 w-3 animate-spin" aria-label="Saving your vote" /> : null}
                {poll.isCreator && !poll.isClosed ? (
                    confirmClose ? (
                        <span className="ml-auto flex items-center gap-1.5">
                            <span>Close for everyone?</span>
                            <button
                                type="button"
                                onClick={() => close.mutate()}
                                disabled={close.isPending}
                                className="rounded-md bg-primary px-2 py-0.5 font-medium text-primary-foreground disabled:opacity-50"
                            >
                                Close poll
                            </button>
                            <button type="button" onClick={() => setConfirmClose(false)} className="rounded-md border px-2 py-0.5 hover:bg-accent">
                                Cancel
                            </button>
                        </span>
                    ) : (
                        <button
                            type="button"
                            onClick={() => setConfirmClose(true)}
                            className="ml-auto rounded-md border px-2 py-0.5 font-medium text-foreground hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                        >
                            Close poll
                        </button>
                    )
                ) : null}
            </footer>
            {error ? <p role="alert" className="mt-1 text-xs text-destructive">{error}</p> : null}
        </section>
    );
}
